import { DataLoader } from '@lessonup/client-integration';
import { ModalHeaderV1, SpaceBetween, spacing, styled, Tab, TabList, TextElement } from '@lessonup/ui-components';
import { getRouteForAsset } from '@lessonup/utils';
import { compact } from 'lodash';
import React, { useState } from 'react';
import { useEditorTranslation } from '../../../../hooks/useEditorTranslation';
import { CopyPanel } from './CopyPanel';
import DownloadLessonById from './DownloadButton';
import { tabConfigs } from './ShareLessonModal.constants';
import { ShareLessonModalInnerProps, ShareLessonTabs } from './ShareLessonModal.types';
import { TabContent } from './TabContent';

const shareWithStudentsDataLoaderCssClass = 'share-with-students-data-loader';

export const ShareLessonModalInner: React.FC<ShareLessonModalInnerProps> = ({
  handleModalClose,
  lessonId,
  isShareWithStudentsEnabled,
  shareLessonsWithStudentsError,
  lessonShareUrl,
  loading = false,
  onCopyTeacherShareUrl,
  onCopyStudentShareUrl,
  onPdfDownloadSuccess,
  shareLessonWithStudentsUrl,
  exportToPdfDirect,
}) => {
  const { t } = useEditorTranslation();
  const [activeTab, setActiveTab] = useState<ShareLessonTabs>(ShareLessonTabs.SHARE_LINK);
  const tabs: ShareLessonTabs[] = compact([
    ShareLessonTabs.SHARE_LINK,
    isShareWithStudentsEnabled ? ShareLessonTabs.SHARE_WITH_STUDENTS : undefined,
    ShareLessonTabs.EXPORT_PDF,
  ]);

  const currentTabConfig = tabConfigs[activeTab];

  return (
    <>
      <ModalHeaderV1
        type="headline"
        showDivider={false}
        title={{ content: t('shareLessonModal.info') }}
        overflow="wrap"
        onCloseButtonClick={handleModalClose}
      />
      <ModalBodyContainer>
        <ModalItemContainer>
          <TabList resizing="fixed">
            {tabs.map((tabId) => {
              const config = tabConfigs[tabId];
              return (
                <Tab
                  key={tabId}
                  as="button"
                  onClick={() => setActiveTab(tabId)}
                  isActive={activeTab === tabId}
                  content={t(config.tabLabelKey)}
                />
              );
            })}
          </TabList>
        </ModalItemContainer>

        <TabContent
          leftImagePath={getRouteForAsset(currentTabConfig.imageAssetPath)}
          rightTitleKey={currentTabConfig.titleKey}
          rightBodyTextKey={currentTabConfig.bodyTextKey}
          banner={currentTabConfig.banner}
          linkTextKey={currentTabConfig.linkTextKey}
          linkUrlKey={currentTabConfig.linkUrlKey}
        />

        <ModalItemContainer>
          <SpaceBetween spacing={spacing.size8} direction="x" alignItems="end" justifyContent="center">
            {activeTab === ShareLessonTabs.SHARE_LINK && (
              <CopyPanel
                name={t('shareLesson.labelForLink')}
                value={lessonShareUrl ?? ''}
                onCopyClick={onCopyTeacherShareUrl}
              />
            )}
            {activeTab === ShareLessonTabs.EXPORT_PDF && (
              <DownloadLessonById
                lessonId={lessonId ?? ''}
                handleSuccess={onPdfDownloadSuccess}
                handleError={handleModalClose}
                handleExport={exportToPdfDirect}
              />
            )}
            {activeTab === ShareLessonTabs.SHARE_WITH_STUDENTS && (
              <DataLoader
                loading={loading}
                error={shareLessonsWithStudentsError}
                data={shareLessonWithStudentsUrl}
                initialErrorDisplayMode="custom"
                customErrorRenderer={() => {
                  return (
                    <TextElement textStyle="bodyText" color="error">
                      {t('shareLessonModal.errorLoadingLink')}
                    </TextElement>
                  );
                }}
                dataRenderer={(data) => (
                  <CopyPanel name={t('shareLesson.labelForLink')} value={data} onCopyClick={onCopyStudentShareUrl} />
                )}
                className={shareWithStudentsDataLoaderCssClass}
              />
            )}
          </SpaceBetween>
        </ModalItemContainer>
      </ModalBodyContainer>
    </>
  );
};

const ModalBodyContainer = styled.div`
  padding: ${spacing.size8};
  min-height: 330px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: 100%;
  overflow: hidden;

  .${shareWithStudentsDataLoaderCssClass} {
    width: 100%;
  }
`;

const ModalItemContainer = styled.div`
  margin: ${spacing.size16};
`;
